import type { Conversation } from '@twilio/conversations';

import { Platform } from 'react-native';

import { ScrollView, XStack, YStack } from 'tamagui';

import { BodyS, Icon, LabelM } from '@south-street-app/atoms';

import { useConversationPreview } from '../ChatScreen/useConversationPreview';

const pressStyle = { opacity: 0.8 };

const formatArchivedTime = (date: Date | null | undefined) => {
  if (!date) {
    return '';
  }

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatArchivedDate = (date: Date | null | undefined) => {
  if (!date) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

type ArchivedConversationListRowProps = {
  conversation: Conversation;
  onPress: (uniqueName: string) => void;
  refreshKey: number;
  isLast?: boolean;
};

const ArchivedConversationListRow = ({
  conversation,
  onPress,
  refreshKey,
  isLast = false,
}: ArchivedConversationListRowProps) => {
  const preview = useConversationPreview(conversation, refreshKey);
  const uniqueName = conversation.uniqueName ?? conversation.sid;
  const timestamp = preview?.dateCreated ?? conversation.lastMessage?.dateCreated;

  return (
    <YStack>
      <XStack
        alignItems={'center'}
        gap={'$3'}
        onPress={() => onPress(uniqueName)}
        paddingHorizontal={'$4'}
        paddingVertical={'$3'}
        pressStyle={pressStyle}
        {...(Platform.OS === 'web' ? { cursor: 'pointer' } : undefined)}
      >
        <YStack
          alignItems={'center'}
          backgroundColor={'$brand500'}
          borderRadius={'$full'}
          height={'$9'}
          justifyContent={'center'}
          width={'$9'}
        >
          <Icon color={'$mono100'} iconName={'southStreetIcon'} size={'$4'} />
        </YStack>

        <YStack flex={1} gap={'$0.5'} minWidth={0}>
          <LabelM color={'$mono900'} fontWeight={'$2'} numberOfLines={1}>
            {conversation.friendlyName ?? 'Trade chat'}
          </LabelM>
          <BodyS color={'$mono500'}>
            {formatArchivedDate(timestamp)}
          </BodyS>
        </YStack>

        <XStack alignItems={'center'} gap={'$2'}>
          <BodyS color={'$mono500'} flexShrink={0}>
            {formatArchivedTime(timestamp)}
          </BodyS>
          <Icon color={'$mono400'} iconName={'chevronRightIcon'} size={'$4'} />
        </XStack>
      </XStack>

      {!isLast ? (
        <YStack
          borderBottomColor={'$mono300'}
          borderBottomWidth={1}
          marginHorizontal={'$4'}
        />
      ) : null}
    </YStack>
  );
};

type ArchivedChatsListProps = {
  conversations: Conversation[];
  onPressConversation: (uniqueName: string) => void;
  refreshKey: number;
  selectedUniqueName?: string;
};

const ArchivedChatsList = ({
  conversations,
  onPressConversation,
  refreshKey,
}: ArchivedChatsListProps) => {
  if (conversations.length === 0) {
    return (
      <YStack alignItems={'center'} flex={1} gap={'$3'} justifyContent={'center'} padding={'$6'}>
        <Icon color={'$mono400'} iconName={'historyIcon'} size={'$8'} />
        <LabelM color={'$mono900'} fontWeight={'$2'}>
          {"No archived chats"}
        </LabelM>
        <BodyS color={'$mono500'} textAlign={'center'}>
          {"Completed trade conversations will appear here."}
        </BodyS>
      </YStack>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <YStack paddingVertical={'$2'}>
        {conversations.map((conversation, index) => (
          <ArchivedConversationListRow
            conversation={conversation}
            isLast={index === conversations.length - 1}
            key={conversation.uniqueName}
            onPress={onPressConversation}
            refreshKey={refreshKey}
          />
        ))}
      </YStack>
    </ScrollView>
  );
};

export { ArchivedChatsList };
